/**
 * Environment configuration with auto-detection
 * Reads Firebase config from environment variables (VITE_*)
 */

export type Environment = 'development' | 'staging' | 'production';

/**
 * Auto-detect environment from hostname
 */
const getEnvironment = (): Environment => {
  if (typeof window === 'undefined') return 'development';

  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }

  if (hostname.includes('staging')) {
    return 'staging';
  }

  return 'production';
};

/**
 * Application configuration
 */
export const config = {
  env: getEnvironment(),

  api: {
    baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000',
  },

  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  },

  // Feature flags
  features: {
    useMock: import.meta.env.VITE_USE_MOCK === 'true',
    enableAnalytics: getEnvironment() === 'production',
    debug: getEnvironment() === 'development',
  },
};

/**
 * Validate required Firebase config
 */
export const validateFirebaseConfig = (): boolean => {
  const required = ['apiKey', 'authDomain', 'projectId'];
  const missing = required.filter(key => !config.firebase[key as keyof typeof config.firebase]);

  if (missing.length > 0) {
    console.error(`Missing Firebase config: ${missing.join(', ')}`);
    return false;
  }

  return true;
};
